import { Check, X } from 'lucide-react';
import type { CorreccionExamen } from './academyApi';

/**
 * La revisión de un examen rendido: una tarjeta por pregunta con lo que contestó
 * el usuario, la correcta cuando corresponde, y la explicación.
 *
 * **Vive en su propio archivo porque lo usan DOS pantallas**
 * (ADR-2026-08-24-la-revision-sobrevive-al-intento): la de resultado, justo
 * después de corregir, y la de revisión, que muestra el último intento guardado.
 * Las dos reciben lo MISMO: el array de correcciones que devuelve el servidor.
 *
 * **Ya no decide qué revelar.** Antes tenía un prop `revelarCorrectas` y el
 * front elegía si mostrar la respuesta buena; ahora esa decisión la toma
 * `academy_rendir_examen` y viaja en los datos: si el intento no aprobó, las
 * correcciones simplemente NO traen `correcta` ni `explicacion`
 * (ADR-2026-08-25-la-academia-vive-en-supabase). No se puede filtrar lo que
 * nunca llegó al navegador.
 */
export function RevisionRespuestas({
  correcciones,
  titulo = 'Revisión de tus respuestas',
}: {
  correcciones: CorreccionExamen[];
  /** El encabezado del bloque — cambia según la pantalla que lo monte. */
  titulo?: string;
}) {
  return (
    <section className="mt-8">
      <h2 className="text-[17px] font-bold tracking-[-0.01em] text-brand-black/90">
        {titulo}
      </h2>

      {/* Dos columnas desde `xl` (ADR-2026-08-24-examen-a-ancho-completo): con
          10 preguntas en una sola columna a ancho completo, la revisión son diez
          tarjetas anchas y bajas que obligan a scrollear una pantalla entera por
          pregunta. `items-start` para que una tarjeta con explicación larga no
          estire a su vecina. */}
      <div className="mt-4 grid items-start gap-3 xl:grid-cols-2">
        {correcciones.map((c, i) => {
          const bien = c.esCorrecta;
          // `correcta` sólo viene si el servidor decidió revelar.
          const revela = c.correcta !== undefined;
          return (
            /* Acierto y error se distinguen por TRES señales a la vez
               (ADR-2026-08-24-la-revision-distingue-acierto-y-error): la tarjeta
               entera tintada —que es lo que se lee de un vistazo al barrer la
               lista—, una chapa con la palabra, y el ícono. La palabra no sobra:
               con sólo color e ícono, quien no distingue rojo de verde se queda
               con dos tildes parecidas. */
            <article
              key={c.preguntaId}
              className={`overflow-hidden rounded-2xl border ${
                bien
                  ? 'border-emerald-200 bg-emerald-50/60'
                  : 'border-red-200 bg-red-50/50'
              }`}
            >
              <div className="p-4 sm:p-5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  {/* El número de la pregunta
                      (ADR-2026-08-24-la-revision-numera-las-preguntas): sin él,
                      diez tarjetas de "Tu respuesta" no se pueden referenciar ni
                      cruzar contra el cuestionario, que sí venía numerado. */}
                  <p className="text-[11.5px] font-semibold uppercase tracking-[0.06em] text-black/40">
                    Pregunta {i + 1} de {correcciones.length}
                  </p>

                  <span
                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11.5px] font-semibold text-white ${
                      bien ? 'bg-emerald-600' : 'bg-red-500'
                    }`}
                  >
                    {bien ? (
                      <Check className="h-3 w-3" strokeWidth={3} />
                    ) : (
                      <X className="h-3 w-3" strokeWidth={3} />
                    )}
                    {bien ? 'Correcta' : 'Incorrecta'}
                  </span>
                </div>

                <h3 className="mt-2.5 text-[15px] font-semibold leading-snug text-brand-black/90">
                  {c.enunciado}
                </h3>

                {/* Lo que respondió — SIEMPRE, también cuando acertó. Sólo la
                    correcta informa cuál era la buena pero no confirma que la
                    haya elegido el usuario. */}
                <div className="mt-3 space-y-1.5">
                  <p
                    className={`flex gap-2 text-[13.5px] leading-relaxed ${
                      bien ? 'text-emerald-900/90' : 'text-red-800/90'
                    }`}
                  >
                    <span className="mt-[3px] shrink-0">
                      {bien ? (
                        <Check className="h-3.5 w-3.5" strokeWidth={2.8} />
                      ) : (
                        <X className="h-3.5 w-3.5" strokeWidth={2.8} />
                      )}
                    </span>
                    <span className="min-w-0">
                      <span className="font-semibold">Tu respuesta:</span>{' '}
                      {c.elegida === null
                        ? 'sin responder'
                        : c.opciones[c.elegida]}
                    </span>
                  </p>

                  {/* La correcta sólo cuando erró —en el acierto sería la misma
                      frase dos veces— y sólo si el servidor la mandó. */}
                  {!bien && revela && (
                    <p className="flex gap-2 text-[13.5px] leading-relaxed text-emerald-900/90">
                      <span className="mt-[3px] shrink-0">
                        <Check className="h-3.5 w-3.5" strokeWidth={2.8} />
                      </span>
                      <span className="min-w-0">
                        <span className="font-semibold">Correcta:</span>{' '}
                        {c.opciones[c.correcta as number]}
                      </span>
                    </p>
                  )}
                </div>
              </div>

              {/* La explicación sobre blanco y no sobre el tinte: es texto neutro
                  —enseña, no juzga— y sobre el fondo de color competía con las
                  respuestas de arriba, que sí llevan el veredicto. */}
              {c.explicacion && (
                <p className="border-t border-black/[0.05] bg-white/70 px-4 py-3 text-[13.5px] leading-relaxed text-black/55 sm:px-5">
                  {c.explicacion}
                </p>
              )}

              {/* Lo que reemplaza a la explicación cuando no se revela: sin esto
                  la tarjeta errada termina de golpe y se lee como si le faltara
                  algo por un error. El copy apunta a lo que el usuario PUEDE
                  hacer, no a lo que la app le está reteniendo
                  (ADR-2026-08-25-el-copy-de-la-errada-apunta-a-repasar). */}
              {!bien && !revela && (
                <p className="border-t border-black/[0.05] bg-white/70 px-4 py-3 text-[12.5px] leading-relaxed text-black/45 sm:px-5">
                  Volvé a repasar la lección para reforzar este punto y responderla
                  bien en el próximo intento.
                </p>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}
